/**
 * The `Readings` catalog component: facts, each with a state beside it.
 *
 * The design's most-used visual — 39 instances across the screens read
 * (13d, 2c) — and the one that carries the least geometry: a label, an
 * optional sub-label, a figure, and a dot saying how the figure stands.
 *
 * NOTHING HERE DECIDES A STATE. The dot's colour is a lookup on a word the
 * server put in the data model, and a word that is not one of the three
 * never arrives: `_readings_parts` drops that row rather than send a state
 * this side would have to invent a colour for. So the fallback below is
 * unreachable by construction and is written anyway, because a renderer
 * that threw on an unexpected string would turn a wire change into a blank
 * transcript.
 *
 * GREEN AND RED BELONG TO THE MARKET, AMBER TO MITRA — the same rule
 * `Withheld` follows. `fine` and `worse` are market judgements and take the
 * market's own two colours; `watch` is Mitra saying to look again, which is
 * exactly what amber means everywhere else on this surface. All three are
 * theme tokens, never hexes, so dark mode is one definition away rather
 * than a second palette.
 *
 * The figure goes through `Fig` like every other number on this surface:
 * grouped two-two-three from the string the engine sent, never re-parsed,
 * with its unit attached and the dagger on a derived figure. A value that
 * is a word rather than a figure ("AAA") passes through the same component
 * and simply has no unit to draw.
 */
import { z } from 'zod/v3'
import type { ComponentApi } from '@a2ui/web_core/v0_9'
import { createComponentImplementation } from '@a2ui/react/v0_9'
import type { CellValue } from '@/features/mitra/lib/figures'
import { captionLabel } from '@/features/mitra/lib/figures'
import type { ReadingState } from '@/features/mitra/api/frames'
import { Fig, Label, SCROLL_X } from '@/features/mitra/panel/blocks/Card'
import { PathBinding } from '@/features/mitra/panel/blocks/charts/Line'

export const ReadingsApi = {
  name: 'Readings',
  schema: z
    .object({
      rows: PathBinding,
      caption: PathBinding.optional(),
    })
    .strict(),
}

/** The shape behind `/rows`, fixed by `to_messages`. */
export type ReadingPart = {
  id: string
  label: string
  sub: string | null
  value: CellValue
  state: ReadingState
}

/** The dot, per state. Tokens, so the dark theme redefines rather than repeats. */
const DOT: Record<string, string> = {
  fine: 'bg-up',
  watch: 'bg-caution',
  worse: 'bg-down',
}

/**
 * The figure's colour, per state.
 *
 * The design tints the NUMBER as well as the dot (13d: an amber 23.1 beside
 * an amber dot, a green 68% beside a green one). A neutral figure next to a
 * coloured dot makes the reader look twice to find which of the two is the
 * verdict. The tone is still the server's — this only looks it up.
 */
const FIGURE_TONE: Record<string, string> = {
  fine: 'text-up',
  watch: 'text-caution',
  worse: 'text-down',
}

function listOf<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : []
}

// Crosses the zod/v3 <-> @a2ui/web_core-zod boundary once — see the comment
// on `Line`'s own `createComponentImplementation` call for why this is a
// named `const` and not an inline cast: eslint's type-aware rules crash on
// the inline form the same way tsc crashes on `PathBinding`'s union.
const readingsApi = ReadingsApi as unknown as ComponentApi

export const Readings = createComponentImplementation<ComponentApi>(
  readingsApi,
  ({ props }: { props: { rows?: unknown; caption?: unknown } }) => (
    <ReadingsList
      rows={listOf<ReadingPart>(props.rows)}
      caption={typeof props.caption === 'string' ? props.caption : null}
    />
  ),
)

/** The drawing, given the list the surface resolved. */
function ReadingsList({ rows, caption }: { rows: ReadingPart[]; caption: string | null }) {
  if (rows.length === 0) {
    return <div data-role="chart-empty" className="px-3 py-2 text-body-xs text-ink-soft" />
  }
  return (
    <figure
      data-role="chart"
      data-component="readings"
      className="m-0 flex flex-col gap-2 px-3 pb-2 pt-2"
    >
      {/* ABOVE THE ROWS, not below them. Every card in the design carries its
          caption as a small caps header — "NEPSE P/E · WHERE 23.1 SITS",
          "THE ARITHMETIC · YOUR 214" — and a caption underneath reads as a
          footnote about a picture rather than as the picture's name. */}
      {caption && (
        <figcaption data-role="caption">
          <Label>{captionLabel(caption)}</Label>
        </figcaption>
      )}
      <ul className="m-0 flex list-none flex-col p-0">
        {rows.map((r, i) => (
          <li
            key={r.id}
            data-role="reading"
            className={`flex flex-wrap items-start gap-x-2 py-2 ${
              i > 0 ? 'border-t border-edge' : ''
            }`}
          >
            <span
              data-role="reading-dot"
              data-state={r.state}
              aria-hidden="true"
              className={`mt-2 h-2 w-2 shrink-0 rounded-full
                          ${DOT[r.state] ?? 'bg-ink-soft'}`}
            />
            {/* STACKED, not inline. The sub-label is a second fact about the
                same reading — "sector median 12.4" under "P/E" — and set
                beside the label it reads as part of the name. */}
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="text-label-l font-medium leading-tight text-ink">{r.label}</span>
              {r.sub && (
                <span
                  data-role="reading-sub"
                  className="mt-0 text-body-xs leading-tight text-ink-soft"
                >
                  {r.sub}
                </span>
              )}
            </span>
            <span className={`block w-full max-w-full pl-4 pt-1 text-left ${SCROLL_X}`}>
              <Fig
                cell={r.value}
                className={`text-body-l font-medium tabular-nums ${
                  FIGURE_TONE[r.state] ?? 'text-ink'
                }`}
              />
            </span>
          </li>
        ))}
      </ul>
    </figure>
  )
}
